import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Form, useForm } from 'react-hook-form';
import { Box, Flex, Modal, ModalContent, ModalOverlay } from '@chakra-ui/react';
import moment from 'moment';

import Canvas from 'UiKit/canvas';
import { TextBold, TextRegular } from 'UiKit/typography';
import { captchaText } from 'Const/monitoring/view';
import ButtonSolid from 'UiKit/button/buttonSolid';
import InputComponent from 'UiKit/input';
import ButtonOutline from 'UiKit/button/buttonOutline';
import { RefreshIcon } from 'Asset/icon';
import CountTimer from 'Component/count-timer';
import { useToast } from 'Store/toast';
import useCaptchaSchedule from 'Store/captchaSchedule';
import { Condition } from 'Component/others';
import usePostAttendanceViolation from 'Domain/monitoring/usePostAttendanceViolation';
import usePostNotification from 'Domain/monitoring/usePostNotifications';

import ViolationAttendanceContent from 'View/monitoring/component/violation-attendance-content';

import styles from './styles.module.css';

const Captcha = ({ monitoringStatus }) => {
  const { handleSubmit, register, control, setValue, watch, reset } = useForm();
  const [numbers, setNumbers] = useState({
    firstNumber: 0,
    secondNumber: 0,
    answer: 0,
  });
  const [isWrongCaptcha, setIsWrongCaptcha] = useState(false);
  const [isExpiredState, setIsExpiredState] = useState(false);
  const [isViolationSent, setIsViolationSent] = useState(localStorage.getItem('isViolationSent') === 'true' || false);
  const { onOpenToast } = useToast();
  const [isOpen, setIsOpen] = useState(false);
  const { inputAnswer } = watch();
  const canvasRef = useRef();
  const captchaTimeoutRef = useRef(null);
  const channelRef = useRef(null);
  const soundRef = useRef(null);
  const soundIntervalRef = useRef(null);
  const { mutate: postViolation } = usePostAttendanceViolation();
  const { mutate: postNotification } = usePostNotification();
  const { action, resetAction } = useCaptchaSchedule();
  const MIN_RANDOM_TIME = 45 * 60 * 1000;
  const MAX_RANDOM_TIME = 60 * 60 * 1000;
  const EXPIRED_TIME = 2 * 60 * 1000;

  const randomInt = (min, max) => Math.floor(Math.random() * (max - min + 1)) + min;

  const randomColor = (isDark = false) => {
    const [min, max] = isDark ? [0, 80] : [100, 200];
    return `rgb(${randomInt(min, max)}, ${randomInt(min, max)}, ${randomInt(min, max)})`;
  };

  const playNotificationSound = useCallback(() => {
    if (!soundRef.current) {
      soundRef.current = new Audio('/sound-notification.mp3');
    }
    soundRef.current.currentTime = 0;
    soundRef.current.play().catch(() => {});
  }, []);

  const stopNotificationSound = useCallback(() => {
    if (soundIntervalRef.current) {
      clearInterval(soundIntervalRef.current);
      soundIntervalRef.current = null;
    }
    if (soundRef.current) {
      soundRef.current.pause();
      soundRef.current.currentTime = 0;
    }
  }, []);

  const encodeCaptchaData = (data) => btoa(JSON.stringify(data));

  const decodeCaptchaData = () => {
    const raw = localStorage.getItem('captchaScheduledTime');
    if (!raw) return null;
    try {
      return JSON.parse(atob(raw));
    } catch {
      return null;
    }
  };

  const generateCaptcha = useCallback(() => {
    if (!canvasRef.current) return;

    const firstNumber = randomInt(1, 20);
    const secondNumber = randomInt(1, 20);
    const answer = firstNumber + secondNumber;
    setNumbers({
      firstNumber,
      secondNumber,
      answer,
    });

    const contextCanvas = canvasRef.current.getContext('2d');
    contextCanvas.fillStyle = 'rgb(240, 240, 240)';
    contextCanvas.fillRect(0, 0, 300, 100);

    contextCanvas.font = 'bold 34px Arial';
    contextCanvas.fillStyle = randomColor(true);
    contextCanvas.fillText(`${firstNumber} + ${secondNumber} = ?`, 50, 60);

    for (let i = 0; i < 30; i++) {
      contextCanvas.strokeStyle = randomColor();
      contextCanvas.beginPath();
      contextCanvas.moveTo(randomInt(0, 300), randomInt(0, 100));
      contextCanvas.lineTo(randomInt(0, 300), randomInt(0, 100));
      contextCanvas.stroke();
    }
    /* eslint-disable-next-line */
  }, []);

  const clearCaptchaSchedule = useCallback(() => {
    if (captchaTimeoutRef.current) {
      clearTimeout(captchaTimeoutRef.current);
      captchaTimeoutRef.current = null;
    }
    localStorage.removeItem('captchaScheduledTime');
  }, []);

  const openCaptchaModal = useCallback(() => {
    generateCaptcha();
    postNotification({
      type: 'CAPTCHA_REMINDER',
    });
    setIsOpen(true);
  }, [generateCaptcha, postNotification]);

  const scheduleCaptcha = useCallback(
    (delayMs) => {
      if (captchaTimeoutRef.current) {
        clearTimeout(captchaTimeoutRef.current);
      }

      captchaTimeoutRef.current = setTimeout(() => {
        setIsExpiredState(false);
        openCaptchaModal();
      }, delayMs);
    },
    [openCaptchaModal],
  );

  const initCaptchaSchedule = useCallback(() => {
    const clockInTime = Date.now();
    const randomDelay = randomInt(MIN_RANDOM_TIME, MAX_RANDOM_TIME);
    const scheduledTime = clockInTime + randomDelay;

    const captchaTimerItem = {
      clockInTime: clockInTime.toString(),
      scheduledTime: scheduledTime.toString(),
      expiredTime: (scheduledTime + EXPIRED_TIME).toString(),
    };
    setIsExpiredState(false);
    localStorage.setItem('captchaScheduledTime', encodeCaptchaData(captchaTimerItem));

    scheduleCaptcha(randomDelay);
  }, [scheduleCaptcha, MIN_RANDOM_TIME, MAX_RANDOM_TIME, EXPIRED_TIME]);

  const onSubmit = (data) => {
    const answer = parseInt(data.inputAnswer, 10);
    if (answer === numbers.answer) {
      setIsOpen(false);
      initCaptchaSchedule();
      channelRef.current?.postMessage({ type: 'CAPTCHA_ANSWERED' });
      onOpenToast({
        status: 'success',
        title: captchaText.successTitleText,
        description: captchaText.successDescriptionText,
        duration: 5,
        alwaysShow: false,
      });
    } else {
      setIsWrongCaptcha(true);
      generateCaptcha();
    }
    reset();
  };

  const setCanvasRef = useCallback(
    (node) => {
      canvasRef.current = node;
      if (node) {
        generateCaptcha();
      }
    },
    [generateCaptcha],
  );

  useEffect(() => {
    if (isOpen) {
      playNotificationSound();
      soundIntervalRef.current = setInterval(playNotificationSound, 10 * 1000);
    } else {
      stopNotificationSound();
    }
    return () => stopNotificationSound();
  }, [isOpen, playNotificationSound, stopNotificationSound]);

  useEffect(() => {
    const channel = new BroadcastChannel('captcha_sync');
    channelRef.current = channel;

    channel.onmessage = ({ data }) => {
      if (data.type === 'CAPTCHA_ANSWERED') {
        if (captchaTimeoutRef.current) {
          clearTimeout(captchaTimeoutRef.current);
          captchaTimeoutRef.current = null;
        }
        setIsOpen(false);
        setIsExpiredState(false);
        reset();
        const captchaData = decodeCaptchaData();
        if (captchaData?.scheduledTime) {
          const remaining = parseInt(captchaData.scheduledTime, 10) - Date.now();
          if (remaining > 0) scheduleCaptcha(remaining);
        }
      }

      if (data.type === 'CAPTCHA_CLEAR') {
        if (captchaTimeoutRef.current) {
          clearTimeout(captchaTimeoutRef.current);
          captchaTimeoutRef.current = null;
        }
        setIsOpen(false);
        setIsExpiredState(false);
        reset();
      }
    };

    return () => {
      channel.close();
      channelRef.current = null;
    };
  }, [scheduleCaptcha, reset]);

  useEffect(() => {
    const status = monitoringStatus?.status;

    if (status === 'CLOCKED_IN_ACTIVE') {
      const captchaScheduledTimeJSON = decodeCaptchaData();

      if (captchaScheduledTimeJSON && captchaScheduledTimeJSON.scheduledTime) {
        const scheduledTime = parseInt(captchaScheduledTimeJSON.scheduledTime, 10);
        const expiredTime = parseInt(captchaScheduledTimeJSON.expiredTime, 10);
        const dateNow = Date.now();
        const remainingTime = scheduledTime - dateNow;

        if (remainingTime > 0) {
          scheduleCaptcha(remainingTime);
        } else if (dateNow > expiredTime) {
          if (!isViolationSent) {
            postViolation({
              violationTime: moment().format('YYYY-MM-DD HH:mm:ss'),
              operatorPresenceId: monitoringStatus?.presence?.id,
            });
            resetCaptchaSchedule();
          } else {
            resetViolationSent();
          }
        } else {
          openCaptchaModal();
        }
      } else {
        initCaptchaSchedule();
      }
    } else {
      clearCaptchaSchedule();
    }

    return () => {
      if (captchaTimeoutRef.current) {
        clearTimeout(captchaTimeoutRef.current);
      }
    };
    /* eslint-disable-next-line */
  }, [monitoringStatus, scheduleCaptcha, openCaptchaModal, clearCaptchaSchedule, initCaptchaSchedule]);

  useEffect(() => {
    if (action === 'clear') {
      clearCaptchaSchedule();
      channelRef.current?.postMessage({ type: 'CAPTCHA_CLEAR' });
      resetAction();
    } else if (action === 'init') {
      initCaptchaSchedule();
      resetAction();
    }
  }, [action, clearCaptchaSchedule, initCaptchaSchedule, resetAction]);

  const countdownTimer = useMemo(() => {
    if (!isOpen) return 0;

    const captchaScheduledTimeJSON = decodeCaptchaData();

    if (captchaScheduledTimeJSON && captchaScheduledTimeJSON.expiredTime) {
      const expiredTime = parseInt(captchaScheduledTimeJSON.expiredTime, 10);
      const remainingTimeMs = expiredTime - Date.now();
      const remainingTimeSeconds = Math.floor(remainingTimeMs / 1000);
      return remainingTimeSeconds > 0 ? remainingTimeSeconds : 0;
    }
    return 0;
  }, [isOpen]);

  const isExpired = useMemo(() => {
    if (!isOpen) return false;
    if (isExpiredState) return true;

    const captchaScheduledTimeJSON = decodeCaptchaData();

    if (captchaScheduledTimeJSON && captchaScheduledTimeJSON.expiredTime) {
      const expiredTime = parseInt(captchaScheduledTimeJSON.expiredTime, 10);
      return Date.now() > expiredTime;
    }
    return false;
  }, [isOpen, isExpiredState]);

  const onCloseViolation = () => {
    setIsOpen(false);
    setIsExpiredState(false);
    resetViolationSent();
  };

  const onTimeUp = () => {
    setIsExpiredState(true);
    if (localStorage.getItem('isViolationSent') === 'true') return;
    localStorage.setItem('isViolationSent', 'true');
    setIsViolationSent(true);
    postViolation({
      violationTime: moment().format('YYYY-MM-DD HH:mm:ss'),
      operatorPresenceId: monitoringStatus?.presence?.id,
    });
    resetCaptchaSchedule();
  };

  const resetViolationSent = useCallback(() => {
    setIsViolationSent(false);
    localStorage.removeItem('isViolationSent');
  }, []);

  const resetCaptchaSchedule = useCallback(() => {
    clearCaptchaSchedule();
    initCaptchaSchedule();
  }, [clearCaptchaSchedule, initCaptchaSchedule]);

  return (
    <Modal isOpen={isOpen}>
      <ModalOverlay />
      <ModalContent maxWidth={!isExpired ? '500px' : '350px'} className={styles['captcha__container--content']}>
        <Condition statement={!isExpired} fallback={<ViolationAttendanceContent onClose={onCloseViolation} />}>
          <Box className={styles['captcha__title--box']}>
            <TextBold className={styles['captcha__title--text']}>{captchaText.titleText}</TextBold>
          </Box>
          <Box className={styles['captcha__description--box']}>
            <TextRegular className={styles['captcha__description--text']}>{captchaText.descriptionText}</TextRegular>
          </Box>
          <Form className={styles['captcha__content--box']} onSubmit={handleSubmit(onSubmit)} control={control}>
            <Flex flexDirection={'column'} gap={'24px'}>
              <Box className={styles['captcha__box--canvas']}>
                <Canvas ref={setCanvasRef} width="300" height="102"></Canvas>
                <ButtonOutline onClick={generateCaptcha}>
                  <RefreshIcon color="var(--color-info70)" size="12px" />
                </ButtonOutline>
              </Box>
              <Box>
                <InputComponent
                  placeholder={captchaText.placeholderAnswerInputText}
                  register={{
                    ...register('inputAnswer', {
                      required: 'Answer required!',
                    }),
                  }}
                  error={isWrongCaptcha ? { message: captchaText.wrongCaptchaText } : null}
                  value={inputAnswer}
                  onChange={(e) => setValue('inputAnswer', e.target.value)}
                />
              </Box>
            </Flex>
            <Box className={styles['captcha__footer--box']}>
              <ButtonSolid type="submit">{captchaText.sendButtonText}</ButtonSolid>
              <CountTimer variant="count-down" initialTime={countdownTimer} isEnabled className={styles['captcha__count-down--text']} onTimeUp={onTimeUp} />
            </Box>
          </Form>
        </Condition>
      </ModalContent>
    </Modal>
  );
};

export default Captcha;
